// The persistent application chrome: brand, primary nav, signed-in identity.
//
// It lives OUTSIDE the router's outlet deliberately. The router calls
// `outlet.replaceChildren()` on every navigation, so anything inside would be
// destroyed and rebuilt on each view change — costing a flash of empty nav and,
// worse, a `/me` request per navigation. As a sibling it survives untouched and
// only re-marks its active link, via the router's afterRender hook.
//
// Nothing here is a security boundary. The Admin link is hidden for a non-admin
// as a courtesy; the server rejects the routes regardless, and the admin view
// handles a 403 on its own.

import { endMasquerade, fetchMe, setMasqueradeMode } from './api/client.js';
import { getMasquerade, getRefreshToken } from './api/tokens.js';
import { afterRender, goBack, navigate } from './router.js';
import { brandLogo, brandName, isSocialTie } from './brand.js';
import { el } from './dom.js';
import {
  SiteRole,
  type CurrentUser,
  type MasqueradeState,
} from '@msc/shared';
import { headerBell } from './views/headerBell.js';
import { isNative } from './platform.js';
import { isDesktopView, setDesktopView } from './viewMode.js';
import { icon, type IconName } from './icons.js';

interface NavLink {
  href: string;
  label: string;
  icon: IconName;
}

// The four tabs. No Home entry: the brand mark is the home link. No Admin entry
// either -- it is not a destination most members have, so it lives in the top
// row beside the logo instead of costing every member a tab.
const LINKS: NavLink[] = [
  { href: '/groups', label: 'Groups', icon: 'groups' },
  { href: '/events', label: 'Events', icon: 'events' },
  { href: '/calendar', label: 'Calendar', icon: 'calendar' },
  { href: '/contacts', label: 'Contacts', icon: 'contacts' },
];

/**
 * The SocialTie tabs -- two jobs, not four.
 *
 * Same app, same account, same API; what differs is which name was typed. A
 * visitor who went to socialtie.com came for contacts, so the events half of the
 * product is not repeated here. This is the two-surface rule from
 * `docs/plan-address-book.md` applied to the nav itself: FEWER tasks.
 *
 * No cross-link to mysocialcal.com: the two names are separate origins and
 * therefore separate sessions, so such a link lands a signed-in user on a login
 * screen. The signed-OUT landing page carries the pointer instead.
 */
const SOCIALTIE_LINKS: NavLink[] = [
  { href: '/contacts', label: 'Address book', icon: 'contacts' },
  { href: '/family-tree', label: 'Family tree', icon: 'tree' },
];

/**
 * Screens that get NO back chevron: the tab roots and home. Everything else is
 * a screen pushed on top of one of these, and gets the chevron.
 */
const ROOT_PATHS = new Set(['/', '/groups', '/events', '/calendar', '/contacts', '/family-tree']);


/**
 * Paths that render without chrome.
 *
 * The signed-out screens get none, and neither do the signed one-click links:
 * a day-of confirmation tapped from a phone with no session should show the
 * outcome and nothing else. Wrapping it in a nav full of links that all bounce
 * to /login would be worse than no nav at all.
 */
const BARE_PATHS = new Set([
  '/login',
  '/register',
  '/verify',
  '/forgot',
  '/reset',
  '/confirm',
  '/unsubscribe',
  // Reached from a link in an email, by definition on a device that may hold no
  // session. Rendering the signed-in nav around it would be a row of links that
  // all bounce to /login.
  '/email-preferences',
  // Where a just-deleted account lands. Terminal and signed out by definition,
  // so the ordinary nav would be a row of links that all bounce to /login --
  // and the page carries its own way onward (/register, /privacy, /contact).
  '/goodbye',
  // The first-run flow. Focused, and every step carries its own way onward.
  '/welcome',
  // NOT /privacy, /terms or /contact. They stay in main.ts's PUBLIC_PATHS, so a
  // signed-out visitor still reaches them and the `if (!user)` branch below
  // renders them bare — but a signed-in reader gets the ordinary header and
  // footer instead of being dumped on a page with no way back into the app.
]);

/**
 * Bare paths that carry an id, so they cannot be exact strings.
 *
 * Only the tournament projector view so far: it is pointed at a room on a big
 * screen, where a nav bar is a row of links nobody can click and a footer is a
 * privacy policy in 40-point type. Like /goodbye it carries its own way back,
 * which is what makes hiding the chrome safe rather than a trap.
 */
const BARE_PATTERNS = [
  /^\/events\/\d+\/tournament\/board$/,
  // A join link, opened from a text thread by someone who may have no account.
  // Carries its own way onward (sign up, log in, or join).
  /^\/join\/[^/]+$/,
];

function isBarePath(path: string): boolean {
  return BARE_PATHS.has(path) || BARE_PATTERNS.some((re) => re.test(path));
}

/**
 * The cached identity behind the nav.
 *
 * `undefined` means "not fetched yet" and `null` means "fetched, nobody is
 * signed in" — the same distinction the token store draws, and for the same
 * reason: collapsing them makes a cold start look like a sign-out and would
 * flash a signed-out nav at every returning user.
 */
let cachedUser: CurrentUser | null | undefined;
let inFlight: Promise<CurrentUser | null> | null = null;

async function currentUser(): Promise<CurrentUser | null> {
  // A cached null with a token now present means someone just signed in — the
  // login screen cached "nobody" a moment before the token existed. Without this
  // the nav stays hidden until a full reload, which is exactly what it looks
  // like when the whole feature is broken.
  const stale = cachedUser === null && getRefreshToken() !== null;
  if (cachedUser !== undefined && !stale) return cachedUser;

  if (!getRefreshToken()) {
    cachedUser = null;
    return null;
  }
  // De-duplicated: the first render can trigger two nav syncs in quick
  // succession, and two /me calls for one page load is a waste.
  inFlight ??= fetchMe()
    .then((user) => {
      cachedUser = user;
      return user;
    })
    .catch(() => {
      cachedUser = null;
      return null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Drop the cached identity. Call on login and logout, or the nav goes stale. */
export function invalidateChrome(): void {
  cachedUser = undefined;
}

/**
 * The current `sync`, so `refreshChrome()` can re-run it without a navigation.
 *
 * Module-level because `sync` is a closure over the mounted header, and the
 * header is mounted once.
 */
let resync: ((path: string) => void) | null = null;

/**
 * Drop the cached identity AND redraw the header now.
 *
 * `invalidateChrome()` alone only clears the cache; the header is redrawn by
 * `afterRender`, so it does not actually change until the next NAVIGATION. That
 * is invisible for login and logout, which both navigate immediately, and wrong
 * for anything edited in place on the account page -- upload a photo or change
 * your first name and the header keeps showing the old one until you happen to
 * click something else.
 */
export function refreshChrome(): void {
  invalidateChrome();
  resync?.(location.pathname);
}

/**
 * The signed-in viewer's own id, for anything that needs to know "is this
 * mine" client-side — e.g. deriving which reaction the viewer picked from a
 * post's public roster. Shares `currentUser()`'s cache and de-duped in-flight
 * fetch, so calling this costs nothing extra beyond the nav's own lookup.
 */
export async function getCurrentUserId(): Promise<number | null> {
  const user = await currentUser();
  return user?.userId ?? null;
}

/**
 * Is `href` the active nav entry for `path`?
 *
 * Prefix matching, so `/groups/80/events` still lights up "Groups" — but never
 * for '/', which is a prefix of everything and would otherwise always match.
 */
function isActive(href: string, path: string): boolean {
  if (href === '/') return path === '/';
  return path === href || path.startsWith(`${href}/`);
}

/**
 * The masquerade banner.
 *
 * It lives in the chrome for one reason above all: the chrome sits outside the
 * router's outlet, so the banner survives every navigation without being rebuilt.
 * An admin acting as somebody else must never reach a screen that does not say so
 * — a banner that can be navigated away from is worse than none, because it
 * teaches the admin to trust its absence.
 */
function masqueradeBanner(state: MasqueradeState, rerender: () => void): HTMLElement {
  const writing = state.mode === 'write';

  const modeBtn = el(
    'button',
    { type: 'button', class: 'btn btn-link masq-action' },
    writing ? 'Back to read-only' : 'Enter write mode',
  );
  modeBtn.addEventListener('click', () => {
    // Only entering write mode asks. Going back to read-only is the safe
    // direction and does not need a speed bump.
    if (
      !writing &&
      !confirm(
        `Enter write mode as ${state.subjectName}?

` +
          `Changes you make will be recorded as theirs, though the audit log will ` +
          `name you. Emails, texts, and push are suppressed either way, and you ` +
          `still cannot change their password, close an event, delete a group, or ` +
          `remove a member.`,
      )
    ) {
      return;
    }
    modeBtn.setAttribute('disabled', 'disabled');
    void setMasqueradeMode(writing ? 'read' : 'write')
      .then(rerender)
      .finally(() => modeBtn.removeAttribute('disabled'));
  });

  const endBtn = el('button', { type: 'button', class: 'btn btn-link masq-action' }, 'Stop');
  endBtn.addEventListener('click', () => {
    endBtn.setAttribute('disabled', 'disabled');
    void endMasquerade()
      .then(() => {
        invalidateChrome();
        // Back to the admin's own Users list, which is where they started and the
        // only screen that is certainly still theirs to see.
        navigate('/admin/users', true);
        rerender();
      })
      .finally(() => endBtn.removeAttribute('disabled'));
  });

  return el('div', { class: writing ? 'masq-banner masq-write' : 'masq-banner', role: 'status' }, [
    el('strong', {}, `Viewing as ${state.subjectName}`),
    el(
      'span',
      { class: 'masq-mode' },
      writing ? 'WRITE MODE: changes are real' : 'read-only',
    ),
    modeBtn,
    endBtn,
  ]);
}

/**
 * The info menu: the four links the footer used to carry.
 *
 * A popover off an icon rather than a footer, because a footer under a fixed
 * tab bar is a row nobody scrolls far enough to find. Closes on outside click,
 * on Escape, and when one of its links is followed.
 */
function infoMenu(): HTMLElement {
  const button = el(
    'button',
    {
      type: 'button',
      class: 'icon-button',
      'aria-label': 'About and help',
      'aria-expanded': 'false',
      'aria-haspopup': 'true',
    },
    [icon('info')],
  );
  // Shows the wide layout on a phone, for demos. The label states the current
  // state so nobody is left in it without knowing why.
  const viewToggle = el('button', { type: 'button', class: 'info-toggle' });
  const paintToggle = (): void => {
    viewToggle.textContent = isDesktopView() ? 'Desktop view: on' : 'Desktop view: off';
    viewToggle.setAttribute('aria-pressed', String(isDesktopView()));
  };
  paintToggle();
  viewToggle.addEventListener('click', () => {
    setDesktopView(!isDesktopView());
    paintToggle();
  });
  const panel = el('div', { class: 'info-panel', hidden: true }, [
    el('a', { href: '/privacy' }, 'Privacy Policy'),
    el('a', { href: '/terms' }, 'Terms and Conditions'),
    el('a', { href: '/contact' }, 'Contact Us'),
    // Web only: inside the app it would offer to download what is running.
    ...(isNative() ? [] : [el('a', { href: '/app' }, 'Get the Phone App')]),
    viewToggle,
  ]);
  const wrap = el('div', { class: 'info-menu' }, [button, panel]);

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    button.setAttribute('aria-expanded', String(open));
  };
  button.addEventListener('click', () => setOpen(panel.hidden));
  panel.addEventListener('click', (ev) => {
    if ((ev.target as HTMLElement).closest('a')) setOpen(false);
  });
  document.addEventListener('click', (ev) => {
    if (!panel.hidden && !wrap.contains(ev.target as Node)) setOpen(false);
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && !panel.hidden) {
      setOpen(false);
      button.focus();
    }
  });
  return wrap;
}

/**
 * Hide the tab bar while scrolling down, bring it back on any scroll up.
 *
 * A small dead band keeps a finger's jitter from flickering it, and it never
 * hides within the first 80px, so a short page cannot lose its navigation.
 */
function hideOnScroll(bar: HTMLElement): void {
  let lastY = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      const dy = y - lastY;
      if (Math.abs(dy) < 8) return;
      bar.classList.toggle('tab-bar-away', dy > 0 && y > 80);
      lastY = y;
    },
    { passive: true },
  );
  // A new screen starts with its navigation showing.
  afterRender(() => {
    bar.classList.remove('tab-bar-away');
    lastY = window.scrollY;
  });
}

/**
 * Build the chrome, append it before `mount`, and return the element the router
 * should treat as its outlet.
 *
 * Two navs from one list: the header's inline links on a wide screen, and the
 * bottom tab bar on a phone. CSS shows exactly one of them at any width, so
 * neither surface ever holds a destination the other lacks.
 */
export function mountChrome(mount: HTMLElement): HTMLElement {
  const header = el('header', { class: 'site-header', hidden: true });
  const outlet = el('div', { class: 'site-outlet' });
  const tabBar = el('nav', { class: 'tab-bar', hidden: true, 'aria-label': 'Sections' });

  mount.replaceChildren(header, outlet, tabBar);
  hideOnScroll(tabBar);

  // Built once: they hold document-level listeners and a polling timer, and
  // rebuilding them per navigation would stack a new set each time.
  const info = infoMenu();
  const bell = headerBell();

  // Header and tab bar appear and disappear together -- there is no state where
  // one is right and the other is not.
  const setChromeVisible = (visible: boolean): void => {
    header.hidden = !visible;
    tabBar.hidden = !visible;
    document.documentElement.classList.toggle('has-tab-bar', visible);
    if (!visible) {
      header.replaceChildren();
      tabBar.replaceChildren();
    }
  };

  const sync = (path: string): void => {
    if (isBarePath(path)) {
      setChromeVisible(false);
      return;
    }

    void currentUser().then((user) => {
      // A signed-out visitor on a non-bare path is either mid-redirect to /login
      // or reading a public page like /privacy. Either way a nav full of links
      // that all bounce to /login is worse than no nav.
      if (!user) {
        setChromeVisible(false);
        return;
      }

      const isAdmin = user.siteRole >= SiteRole.ADMIN;
      const navLinks = isSocialTie() ? SOCIALTIE_LINKS : LINKS;

      const navLink = (href: string, label: string): HTMLElement =>
        el(
          'a',
          {
            href,
            class: isActive(href, path) ? 'nav-link nav-active' : 'nav-link',
            'aria-current': isActive(href, path) ? 'page' : undefined,
          },
          label,
        );

      tabBar.replaceChildren(
        ...navLinks.map((l) =>
          el(
            'a',
            {
              href: l.href,
              class: isActive(l.href, path) ? 'tab tab-active' : 'tab',
              'aria-current': isActive(l.href, path) ? 'page' : undefined,
            },
            [icon(l.icon), el('span', { class: 'tab-label' }, l.label)],
          ),
        ),
      );

      const back = el(
        'button',
        { type: 'button', class: 'icon-button back-button', 'aria-label': 'Back' },
        [icon('back')],
      );
      back.addEventListener('click', goBack);

      const displayName = user.firstName || user.email || 'Account';
      const masq = getMasquerade();

      header.replaceChildren(
        el('div', { class: 'site-header-inner' }, [
          ...(ROOT_PATHS.has(path) ? [] : [back]),
          el(
            'a',
            {
              // No active class: the brand is deliberately never highlighted.
              // aria-current stays, so the home position is still announced.
              class: 'brand',
              href: '/',
              'aria-current': path === '/' ? 'page' : undefined,
            },
            // The logo lockup already contains the wordmark, so there is no text
            // beside it. The alt text carries the name for anything that cannot
            // see the image.
            [el('img', { class: 'brand-logo', src: brandLogo(), alt: brandName() })],
          ),
          // Admins only, in the top row rather than as a tab: it is not a
          // destination most members have.
          ...(isAdmin ? [navLink('/admin', 'Admin')] : []),
          el(
            'nav',
            { class: 'site-nav', 'aria-label': 'Sections' },
            navLinks.map((l) => navLink(l.href, l.label)),
          ),
          el('div', { class: 'site-user' }, [
            info,
            bell,
            // The avatar and the name are ONE link to /account. Logout lives on
            // that page now; it never needed to be one tap away on every screen.
            el('a', { class: 'nav-link account-link', href: '/account', title: 'Your account' }, [
              user.avatarThumbUrl
                ? el('img', { class: 'account-avatar', src: user.avatarThumbUrl, alt: '' })
                : el(
                    'span',
                    { class: 'account-avatar account-initial', 'aria-hidden': 'true' },
                    (displayName[0] ?? '?').toUpperCase(),
                  ),
              el('span', { class: 'account-name' }, displayName),
            ]),
          ]),
        ]),
        // Directly beneath the header bar, above every view. Inside the same
        // <header> so it cannot be scrolled away from independently.
        ...(masq ? [masqueradeBanner(masq, () => sync(path))] : []),
      );
      setChromeVisible(true);
    });
  };

  resync = sync;
  afterRender(sync);

  /**
   * Publish the header's height as `--msc-header-h`, so a second sticky element
   * can pin BELOW it rather than under it.
   *
   * The header is `position: sticky; top: 0`, and its height is content-driven —
   * it changes when the masquerade banner appears, and when the nav wraps on a
   * narrow screen. The PHP app measured it once on load for the same reason
   * (`sticky_panel.js` set `top` from `.site-header`.outerHeight()); a
   * ResizeObserver keeps it right afterwards instead of only at boot.
   */
  const publishHeaderHeight = () => {
    document.documentElement.style.setProperty(
      '--msc-header-h',
      `${header.hidden ? 0 : header.offsetHeight}px`,
    );
  };
  publishHeaderHeight();
  afterRender(publishHeaderHeight);
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(publishHeaderHeight).observe(header);
  }

  return outlet;
}
